/**
 * CreateFlow: the single shared, focused create modal used by New Site and
 * New Test. It lives in a neutral location and stays domain-agnostic; every
 * Firestore write that differs between Sites (url doc only) and Tests (url doc
 * plus a pluginUserTesting bridge, plus a userTesting config and tasks) is
 * injected through the `actions` prop, so this component never imports either
 * domain's data layer.
 *
 * Shape:
 *   Step 1  Choose a source   (pick-and-proceed: selecting advances at once)
 *   Step 2  Set it up         (per source; Back returns to step 1)
 *
 * Sources: Drop files (primary), From an AI agent, From Figma; Tests also get
 * "One of your sites" when the account has at least one live html site. Tests
 * additionally get a skippable Tasks block on the relevant sources.
 *
 * Commit-on-finish: the real item is only committed when a source finishes.
 * Closing mid-flow persists a resumable draft (the host's createDraft made the
 * draft doc when the first source was picked; persistDraft saves the in-flight
 * name/tasks/source). The draft is never publicly live (Piece 1).
 *
 * Injected actions (all Promise-returning unless noted):
 *   createDraft({ source, name })        -> { urlId }   create the draft doc
 *   persistDraft({ urlId, meta })                        save name/tasks/source
 *   finishDrop({ urlId, deployResult, name, tasks }) -> { landing }
 *   finishFigmaEmbed({ urlId, link, name, tasks })   -> { landing }
 *   finishPublishedSite({ siteId, name, tasks })     -> { landing }
 *   watchLive({ urlId }, onLive) -> unsubscribe        fires onLive(landing)
 *                                                       when a real deploy lands
 *   discard({ urlId })                                   delete the draft
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import FigmaShareHint from '../Aero/FigmaShareHint';
import { checkFigmaLink } from '../../utils/figmaLinkCheck';
import { convertPastedLink } from '../../utils/figmaPaste';
import AeroFigmaLinkField from '../Aero/AeroFigmaLinkField';
import ReactDOM from 'react-dom';
import { useDropzone } from 'react-dropzone';
import firebase from '../../firebase';
import { getFilesFromEvent, gatherFiles } from '../../utils/htmlDeploy/gatherFiles';
import { deploySite } from '../../utils/htmlDeploy/deploySite';
import { generateApiToken } from '../../utils/apiTokens';
import { generateReadableSlug } from '../../pages/AeroSites/sitesUtils';
import TasksEditor from './TasksEditor';
import BuildWithAI from './BuildWithAI';
import './CreateFlow.css';

// Figma export plugins for the "Export with a plugin" path. External links;
// the user exports HTML in Figma, then drops it back into the standard flow.
const PLUGINS = [
  {
    label: 'Anima',
    desc: 'Best overall quality. 5 exports/day free. Exports a clean HTML/CSS zip.',
    href: 'https://www.animaapp.com',
  },
  {
    label: 'Figma to Code',
    desc: 'Open source, completely free, no limits. Output benefits from a quick cleanup pass in Claude.',
    href: 'https://www.figma.com/community/plugin/842128343887142055',
  },
  {
    label: 'pxCode',
    desc: 'Visual editor for higher-fidelity HTML. Free basic tier.',
    href: 'https://www.pxcode.io',
  },
];

// Default an upload name from what was dropped (single .zip filename, or the
// shared top-level folder name), mirroring the existing upload flows.
function suggestName(files) {
  if (!files || files.length === 0) return '';
  if (files.length === 1 && /\.zip$/i.test(files[0].name || '')) {
    return files[0].name.replace(/\.zip$/i, '');
  }
  const first = String(files[0].path || files[0].webkitRelativePath || '');
  const slash = first.indexOf('/');
  return slash > 0 ? first.slice(0, slash) : '';
}

// The create-user-test tail for a test. After the deploy curl, one more curl
// POSTs to the plain REST endpoint with the same bearer token — no MCP
// registration, so it works in any agent with a shell and network. When tasks
// are pre-filled (Drop / Embed paths) they are inlined in the JSON body;
// otherwise the agent is told to ask for 1 to 5 tasks first, so the
// agent/convert screens need no tasks UI.
function buildTestTail({ token, siteId, kind, tasks }) {
  if (kind !== 'test') return [];
  const taskList = (tasks || []).map((t) => t.trim()).filter(Boolean);
  return [
    '',
    taskList.length
      ? `Then create a recorded user test on that site: POST to https://aeroapp.ai/api/create-user-test with headers Authorization: Bearer ${token} and Content-Type: application/json, and JSON body {"siteId":"${siteId}","tasks":${JSON.stringify(taskList)}}. Reply with the live URL and the test share link from the response.`
      : `Then create a recorded user test on that site. First ask me for 1 to 5 short task descriptions for testers (only skip this for an explicitly exploratory test). Then POST to https://aeroapp.ai/api/create-user-test with headers Authorization: Bearer ${token} and Content-Type: application/json, and JSON body {"siteId":"${siteId}","tasks":["<task 1>","<task 2>"]}. Reply with the live URL and the test share link from the response.`,
  ];
}

// Aero hosting is reached by calling its REST API directly with curl and the
// minted bearer token — no install, no MCP registration, so it works in any
// agent with a shell and network (Cowork, Claude Code, Cursor, Codex). `figma`
// switches to the Figma-convert variant (read the design via the real Figma
// connector, build responsive HTML, deploy in one shot); its value, when a
// string, is the design link to interpolate.
function buildAgentPrompt({ token, siteId, kind, tasks, figma }) {
  const aeroBlock = [
    'Build this project if needed, then zip the build output with index.html at the zip root and deploy it to Aero by calling its HTTP API directly. Do not install anything.',
    '',
    `Upload inline: POST the zip to https://aeroapp.ai/api/deploy-site?siteId=${siteId} with headers Authorization: Bearer ${token} and Content-Type: application/zip. If the response says the upload is too large (sites over ~4MB, e.g. image-heavy folders), use the signed-URL flow instead: POST the file manifest to https://aeroapp.ai/api/deploy-upload-urls, PUT each file to the signed URLs it returns, then POST https://aeroapp.ai/api/deploy-finalize. Each JSON response describes the exact next step.`,
    '',
    'Reply with the live URL.',
  ];
  if (figma) {
    const ref = typeof figma === 'string' && figma ? figma : 'the Figma file I have open';
    return [
      `Read my Figma design at ${ref} using the Figma connector. Convert it to a single self-contained, responsive index.html — all CSS inline, no external files, with solid CSS placeholder colours for any images.`,
      '',
      ...aeroBlock,
      ...buildTestTail({ token, siteId, kind, tasks }),
    ].join('\n');
  }
  return [
    ...aeroBlock,
    ...buildTestTail({ token, siteId, kind, tasks }),
  ].join('\n');
}

const ICONS = {
  drop: (
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
  ),
  agent: (
    <>
      <polyline points="4 17 10 11 4 5" />
      <line x1="12" y1="19" x2="20" y2="19" />
    </>
  ),
  figma: (
    <>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </>
  ),
  published: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </>
  ),
};

function CreateFlow({
  user,
  kind = 'site',
  heading,
  publishedSites = null,
  existingDraft = null,
  initialSource = null,
  actions,
  onClose,
  onFinished,
  // v3 (phase 3b): 'page' renders the flow inside a route (/sites/new,
  // /user-testing/new) instead of a modal: the three ways stay visible as
  // tiles, the chosen one opens below them, and "what happens next" sits on
  // the right. Same state, same handlers, same draft behaviour.
  layout = 'modal',
}) {
  const isPage = layout === 'page';
  const isTest = kind === 'test';
  const uid = user?.uid;

  const [step, setStep] = useState(1);
  const [source, setSource] = useState(null);
  const [figmaMode, setFigmaMode] = useState(null); // null | 'embed' | 'convert' | 'plugin'
  const [convertExit, setConvertExit] = useState(null); // null | 'drop' | 'agent'
  const [aiTool, setAiTool] = useState('claude'); // 'claude' | 'chatgpt' (convert flow)
  const [name, setName] = useState(existingDraft?.name || '');
  const [tasks, setTasks] = useState(['']);
  const [urlId, setUrlId] = useState(existingDraft?.id || null);

  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  const [droppedFiles, setDroppedFiles] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [figmaLink, setFigmaLink] = useState('');

  // Agent path. The address is picked up front (read-only check, no doc), but
  // the draft doc and the publish key are only made when the prompt is copied.
  const [agentSlug, setAgentSlug] = useState(null);
  const [agentPrompt, setAgentPrompt] = useState(null); // the copied text, once copied
  const [preparing, setPreparing] = useState(false);
  const [copied, setCopied] = useState(false);

  const [live, setLive] = useState(false);
  const liveLandingRef = useRef(null);
  const busyRef = useRef(false);
  const finishedRef = useRef(false); // page layout: the flow finished or closed itself
  const unsubRef = useRef(null);
  const nameSuggestionRef = useRef('');

  const hasPublishedSites = Array.isArray(publishedSites) && publishedSites.length > 0;

  const baseSources = [
    { id: 'drop', title: 'Upload HTML', desc: 'Drop a self-contained file or zip. Live in seconds.' },
    { id: 'agent', title: 'From Claude or ChatGPT', tag: 'New', desc: 'Paste one prompt in the chat where you built it.' },
    { id: 'figma', title: 'From Figma', desc: 'Paste your prototype link. Other ways are there too.' },
    ...(isTest && hasPublishedSites
      ? [{ id: 'published', title: 'One of your sites', desc: 'Add a test to a site you already published.' }]
      : []),
  ];
  const sources = baseSources;

  const stopWatch = useCallback(() => {
    if (unsubRef.current) {
      try { unsubRef.current(); } catch (e) { /* ignore */ }
      unsubRef.current = null;
    }
  }, []);

  // Restore a resumed draft into the right step.
  useEffect(() => {
    const meta = existingDraft?.draftMeta;
    if (!meta) return;
    if (meta.source) setSource(meta.source);
    if (meta.figmaMode) setFigmaMode(meta.figmaMode);
    if (Array.isArray(meta.tasks) && meta.tasks.length) setTasks(meta.tasks);
    if (meta.source) setStep(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => stopWatch(), [stopWatch]);

  // Lock background scroll for the modal's lifetime (the scroll container is
  // .aero-main inside the page shell, not document.body).
  useEffect(() => {
    if (isPage) return undefined;
    const scrollEl = document.querySelector('.aero-main');
    const prev = scrollEl ? scrollEl.style.overflow : '';
    if (scrollEl) scrollEl.style.overflow = 'hidden';
    return () => { if (scrollEl) scrollEl.style.overflow = prev; };
  }, [isPage]);

  const currentMeta = () => ({ source, figmaMode, name: name.trim(), tasks });

  // Create the draft doc the first time the user commits to a source, so the
  // flow has a doc to attach progress to and is resumable on close.
  const ensureDraft = useCallback(async (nextSource, slug) => {
    if (urlId) return urlId;
    const res = await actions.createDraft({ source: nextSource, name: name.trim(), slug: slug || undefined });
    setUrlId(res.urlId);
    return res.urlId;
  }, [urlId, actions, name]);

  const selectSource = async (id) => {
    setError(null);
    setSource(id);
    setFigmaMode(id === 'figma' ? 'embed' : null);
    setConvertExit(null);
    setStep(2);
    // The published-site source attaches a test to an EXISTING live site, so
    // it never creates a draft doc; every other source does.
    if (id === 'published') return;
    // Page: nothing is saved by opening a way. The draft appears once there is
    // real input (a prompt copied, a link pasted, files dropped).
    if (isPage) return;
    try {
      await ensureDraft(id);
    } catch (err) {
      console.error('Error starting draft:', err);
      setError('Could not start. Please try again.');
    }
  };

  // Deep-link entry (e.g. the Aero create gate handoff): open directly on a
  // chosen source instead of the source grid. 'agent' -> From Claude or ChatGPT;
  // 'convert' -> From Figma > Convert with AI. Runs once on mount and only when
  // there is no draft to resume; an absent prop = today's behaviour (step 1).
  const initialSourceRef = useRef(initialSource);
  useEffect(() => {
    const want = initialSourceRef.current;
    if (!want || existingDraft) return;
    if (want === 'agent') {
      selectSource('agent');
    } else if (want === 'convert') {
      setSource('figma');
      setFigmaMode('convert');
      setStep(2);
      if (!isPage) {
        ensureDraft('figma').catch((err) => {
          console.error('Error starting draft:', err);
          setError('Could not start. Please try again.');
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goBack = () => {
    stopWatch();
    setError(null);
    setAgentPrompt(null);
    setLive(false);
    // Step back one level at a time inside the Figma sub-tree.
    if (source === 'figma' && convertExit) {
      setConvertExit(null);
      return;
    }
    if (source === 'figma' && figmaMode && figmaMode !== 'embed') {
      setFigmaMode('embed');
      setConvertExit(null);
      return;
    }
    setSource(null);
    setStep(1);
  };

  const requestClose = useCallback(async () => {
    if (busyRef.current) return;
    stopWatch();
    if (urlId) {
      try { await actions.persistDraft({ urlId, meta: currentMeta() }); } catch (e) { /* ignore */ }
    }
    finishedRef.current = true; // already saved: the page's unmount save is skipped
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlId, actions, onClose, stopWatch, source, figmaMode, name, tasks]);

  // Close on Escape, re-binding whenever requestClose changes so it always
  // sees the latest draft state.
  useEffect(() => {
    if (isPage) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose, isPage]);

  // Page only: leaving the page any other way (the header's Cancel, the
  // sidebar, Back in the browser) saves the in-flight draft exactly as closing
  // the modal does. Skipped once the flow has finished.
  const latestRef = useRef({});
  latestRef.current = { urlId, meta: { source, figmaMode, name: name.trim(), tasks } };
  useEffect(() => {
    if (!isPage) return undefined;
    return () => {
      stopWatch();
      const { urlId: id, meta } = latestRef.current;
      if (id && !finishedRef.current && !busyRef.current) {
        actions.persistDraft({ urlId: id, meta }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const finish = (landing) => {
    finishedRef.current = true;
    if (onFinished) onFinished(landing);
  };

  // Page only: bring the opened way into view (on a phone it sits below the
  // three tiles). Not on the first render, so a resumed draft keeps its place.
  const panelRef = useRef(null);
  const openedRef = useRef(false);
  useEffect(() => {
    if (!isPage) return;
    if (!openedRef.current) { openedRef.current = true; return; }
    if (step === 2 && panelRef.current && panelRef.current.scrollIntoView) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isPage, step, source]);

  // Page only: picking another tile while one is open switches to it.
  const switchSource = (id) => {
    if (busy) return;
    if (id === source) {
      if (id === 'figma' && (figmaMode !== 'embed' || convertExit)) {
        setFigmaMode('embed');
        setConvertExit(null);
        setAgentPrompt(null);
        stopWatch();
      }
      return;
    }
    stopWatch();
    setAgentPrompt(null);
    setLive(false);
    selectSource(id);
  };

  // ---- Drop ----
  const onDrop = useCallback((accepted) => {
    if (!accepted || accepted.length === 0) return;
    setDroppedFiles(accepted);
    setError(null);
    if (isPage) ensureDraft('drop').catch(() => {});
    const suggestion = suggestName(accepted);
    setName((prev) => {
      const keepUser = prev.trim() && prev !== nameSuggestionRef.current;
      nameSuggestionRef.current = suggestion;
      return keepUser ? prev : suggestion;
    });
  }, [isPage, ensureDraft]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    getFilesFromEvent,
    disabled: busy,
    multiple: true,
  });

  const runDrop = async () => {
    if (busy || !droppedFiles || droppedFiles.length === 0) return;
    setError(null);
    setBusy(true);
    busyRef.current = true;
    try {
      const { files, homePage } = await gatherFiles(droppedFiles);
      if (files.length === 0) {
        setError('No publishable files found in that drop.');
        return;
      }
      const id = await ensureDraft(source);
      setProgress({ phase: 'hashing', hashed: 0, total: files.length });
      const deployResult = await deploySite({
        uid,
        urlId: id,
        files,
        homePage,
        serveMode: 'multipage',
        makeLive: true,
        onProgress: setProgress,
      });
      // How this site was created, for creation-source tracking. The drop screen
      // is shared by three exits: a plain file drop, a Figma "convert with AI"
      // that exits to a drop, and a plugin export that exits to a drop. Only the
      // convert exit is its own bucket (ai_convert); a plain drop and a plugin
      // export both record as html_drop (plugin exports are indistinguishable
      // from a manual upload at write time).
      const creationSource = figmaMode === 'convert' ? 'ai_convert' : 'html_drop';
      const { landing } = await actions.finishDrop({
        urlId: id,
        deployResult,
        name: name.trim(),
        tasks,
        creationSource,
      });
      finish(landing);
    } catch (err) {
      console.error('Deploy failed:', err);
      setError('Publish failed: ' + err.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
      setProgress(null);
    }
  };

  // ---- Figma embed ----
  const runFigmaEmbed = async () => {
    if (busy || !figmaLink.trim()) return;
    const check = checkFigmaLink(figmaLink);
    if (!check.ok) {
      setError(check.message);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const id = await ensureDraft(source);
      const { landing } = await actions.finishFigmaEmbed({
        urlId: id,
        link: convertPastedLink(figmaLink).link,
        name: name.trim(),
        tasks,
      });
      finish(landing);
    } catch (err) {
      console.error('Figma embed failed:', err);
      setError('Could not set up the prototype: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  // ---- Published site (tests) ----
  const runPublishedSite = async () => {
    if (busy || !selectedSiteId) return;
    setError(null);
    setBusy(true);
    try {
      const { landing } = await actions.finishPublishedSite({
        siteId: selectedSiteId,
        name: name.trim(),
        tasks,
      });
      finish(landing);
    } catch (err) {
      console.error('Create from site failed:', err);
      setError('Could not create the test: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  // ---- Agent ----
  const startWatch = useCallback(async (id) => {
    stopWatch();
    unsubRef.current = await actions.watchLive({ urlId: id }, (landing) => {
      liveLandingRef.current = landing;
      setLive(true);
    });
  }, [actions, stopWatch]);

  // The agent flow's site id: a draft that already exists, else the address
  // picked up front. The site keeps it (e.g. open-chestnut) and the name
  // defaults to it; both can be changed later from the site page.
  const agentSiteId = urlId || agentSlug;
  const agentIsConvert = source === 'figma' && figmaMode === 'convert';
  const agentActive = source === 'agent' || (agentIsConvert && convertExit === 'agent');

  useEffect(() => {
    if (!agentActive || agentSiteId || !uid) return undefined;
    let cancelled = false;
    generateReadableSlug(firebase.firestore())
      .then((slug) => { if (!cancelled) setAgentSlug(slug); })
      .catch(() => { if (!cancelled) setError('Could not get an address ready. Please try again.'); });
    return () => { cancelled = true; };
  }, [agentActive, agentSiteId, uid]);

  // What the box shows before the copy: the real prompt with the key left out.
  const previewPrompt = agentSiteId
    ? buildAgentPrompt({
      token: 'aero_…',
      siteId: agentSiteId,
      kind,
      tasks,
      figma: agentIsConvert ? (figmaLink.trim() || true) : null,
    })
    : null;

  // Copying is the moment the draft is saved and the key is made. The key is
  // minted inside the clipboard promise so the browser still counts the click.
  const buildCopyText = async () => {
    if (agentPrompt) return agentPrompt;
    const id = await ensureDraft(source, agentSlug);
    if (name.trim()) await actions.persistDraft({ urlId: id, meta: currentMeta() });
    const { token, hashedToken, prefix } = await generateApiToken();
    await firebase.firestore()
      .collection('user').doc(uid)
      .collection('apiKeys')
      .add({
        hashedToken,
        label: 'agent-publish',
        prefix,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        lastUsedAt: null,
      });
    const text = buildAgentPrompt({
      token,
      siteId: id,
      kind,
      tasks,
      figma: agentIsConvert ? (figmaLink.trim() || true) : null,
    });
    setAgentPrompt(text);
    startWatch(id);
    return text;
  };

  const copyPrompt = async () => {
    if (preparing || !uid || !agentSiteId) return;
    setPreparing(true);
    setError(null);
    try {
      const pending = buildCopyText();
      if (navigator.clipboard && window.ClipboardItem && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new window.ClipboardItem({ 'text/plain': pending.then((t) => new Blob([t], { type: 'text/plain' })) }),
        ]);
      } else {
        await navigator.clipboard.writeText(await pending);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying prompt:', err);
      setError('Could not copy the prompt. Please try again.');
    } finally {
      setPreparing(false);
    }
  };

  const progressLabel = () => {
    if (!progress) return 'Publishing…';
    if (progress.phase === 'hashing') return `Hashing ${progress.hashed}/${progress.total}…`;
    if (progress.phase === 'uploading') {
      const pct = progress.totalUploadBytes
        ? Math.round((progress.uploadedBytes / progress.totalUploadBytes) * 100)
        : 100;
      return `Uploading ${progress.blobsUploaded}/${progress.blobsTotal} files (${pct}%)…`;
    }
    return 'Publishing…';
  };

  // ---------------------------------------------------------------- render --
  const renderSourceGrid = () => (
    <>
      <div className="ut-create-section-label">Where is it coming from?</div>
      <div className="ut-create-source-grid ut-create-source-grid-auto" role="list">
        {sources.map((s) => (
          <button
            key={s.id}
            type="button"
            className="ut-create-source-card"
            onClick={() => selectSource(s.id)}
          >
            <span className="ut-create-source-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {ICONS[s.id]}
              </svg>
            </span>
            <span className="ut-create-source-title">
              {s.title}
              {s.tag && <span className="aero-tag aero-tag-blue" style={{ marginLeft: 6 }}>{s.tag}</span>}
            </span>
            <span className="ut-create-source-desc">{s.desc}</span>
          </button>
        ))}
      </div>
    </>
  );

  const nameField = (label = 'Name', placeholder = 'e.g., Homepage redesign') => (
    <div className="createflow-field">
      <div className="ut-create-section-label">{label}</div>
      <input
        className="aero-input"
        type="text"
        placeholder={placeholder}
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={busy}
      />
    </div>
  );

  const renderDrop = () => (
    <div className="ut-create-flow">
      <div>
        <div className="ut-create-section-label">{isTest ? 'Prototype files' : 'Site files'}</div>
        <div {...getRootProps({ className: `createflow-dropzone${isDragActive ? ' active' : ''}${busy ? ' disabled' : ''}` })}>
          <input {...getInputProps({ webkitdirectory: 'true', directory: 'true' })} />
          {busy ? (
            <p className="createflow-progress">{progressLabel()}</p>
          ) : isDragActive ? (
            <p>Drop it here</p>
          ) : droppedFiles && droppedFiles.length > 0 ? (
            <>
              <p>{droppedFiles.length} file{droppedFiles.length === 1 ? '' : 's'} ready</p>
              <p className="createflow-dropzone-hint">Drop again or click to replace</p>
            </>
          ) : isPage ? (
            <>
              <svg className="createflow-dropzone-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                {ICONS.drop}
              </svg>
              <p className="createflow-dropzone-title">Drop a folder or zip</p>
              <p className="createflow-dropzone-hint">or choose files. index.html at the top.</p>
            </>
          ) : (
            <p>Drag a folder or .zip of your built {isTest ? 'prototype' : 'site'} here, or click to browse</p>
          )}
        </div>
      </div>
      {!isPage && nameField()}
      {isTest && <TasksEditor tasks={tasks} onChange={setTasks} />}
    </div>
  );

  const renderAgent = () => {
    // Convert-with-AI converges here after BuildWithAI collected the link and
    // tool; the plain agent path enters here directly. Neither asks for a
    // name: the site keeps its generated address and can be renamed later.
    const isConvert = agentIsConvert;
    const toolName = aiTool === 'chatgpt' ? 'ChatGPT' : 'Claude';
    // Figma is a real directory connector added via OAuth, so the convert path
    // still instructs the user to add it. Aero needs no connector: the prompt
    // calls Aero's REST API directly with curl.
    const connectorLine = isConvert
      ? (aiTool === 'chatgpt'
        ? 'In ChatGPT: open Settings, then Apps, and add the Figma integration.'
        : 'In Claude: go to claude.ai/customize/connectors and add the Figma connector.')
      : null;
    const successNote = isConvert
      ? (isTest
        ? `${toolName} converts your design, publishes to Aero, then asks you for tasks in the chat.`
        : `${toolName} converts your design and publishes it to Aero.`)
      : null;
    return (
      <div className="ut-create-flow">
        <ol className="createflow-step-list">
          {connectorLine && <li>{connectorLine}</li>}
          <li>Copy the prompt and paste it into {isConvert ? toolName : 'the chat where you built it'}.</li>
          <li>{isTest ? 'It publishes to Aero, then sets up your test.' : 'It publishes the files and replies with the live link.'}</li>
        </ol>
        {previewPrompt ? (
          <div className="createflow-prompt">
            <pre>{previewPrompt}</pre>
            <button
              type="button"
              className="aero-btn aero-btn-secondary aero-btn-sm createflow-prompt-copy"
              onClick={copyPrompt}
              disabled={preparing}
            >
              {copied ? 'Copied' : 'Copy prompt'}
            </button>
          </div>
        ) : (
          <p className="createflow-note">Getting your address ready…</p>
        )}
        <p className="createflow-note">
          Copying the prompt adds your personal publish key and saves the site as a draft.
        </p>
        {isTest && !isConvert && (
          <p className="createflow-note">Claude will ask you for tasks in the chat when it sets up the test.</p>
        )}
        {agentPrompt && successNote && <p className="createflow-note">{successNote}</p>}
        {agentPrompt && (
          <div className={`createflow-waiting${live ? ' live' : ''}`}>
            {live ? (
              <>It is live. Click Done to open it.</>
            ) : (
              <><span className="createflow-spinner" /> Waiting for your chat to publish…</>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderFigmaEmbed = () => (
    <div className="ut-create-flow">
      <div>
        <div className="ut-create-section-label">Figma prototype link</div>
        <AeroFigmaLinkField
          value={figmaLink}
          onChange={(next) => { setFigmaLink(next); if (error) setError(null); }}
          onBlur={() => { if (isPage && checkFigmaLink(figmaLink).ok) ensureDraft('figma').catch(() => {}); }}
          disabled={busy}
          autoFocus
        />
        <FigmaShareHint withPassword={!isTest} />
        <div className="ut-create-hint">
          One link for now. Add per-screen-size links later in Settings.
        </div>
      </div>
      <details className="ut-create-other-ways">
        <summary>Other ways</summary>
        <div className="ut-create-source-grid" role="list">
          <button type="button" className="ut-create-source-card" onClick={() => setFigmaMode('convert')}>
            <span className="ut-create-source-title">Convert with AI</span>
            <span className="ut-create-source-desc">
              Claude or ChatGPT reads your design and builds real HTML. Needs a paid Figma seat.
            </span>
          </button>
          <button type="button" className="ut-create-source-card" onClick={() => setFigmaMode('plugin')}>
            <span className="ut-create-source-title">Export with a plugin</span>
            <span className="ut-create-source-desc">
              Use Anima, Figma to Code or pxCode inside Figma. No AI subscription needed.
            </span>
          </button>
        </div>
      </details>
      {!isPage && nameField()}
      {isTest && <TasksEditor tasks={tasks} onChange={setTasks} />}
    </div>
  );

  // Convert with AI: BuildWithAI runs the step collection (link -> tool ->
  // name), then converges into the shared renderAgent prompt+watch screen.
  const renderConvert = () => (
    <BuildWithAI
      figmaLink={figmaLink}
      setFigmaLink={setFigmaLink}
      aiTool={aiTool}
      setAiTool={setAiTool}
      onConvergeAgent={() => setConvertExit('agent')}
    />
  );

  // Export with a plugin: three external plugin options, then a dropzone that
  // reuses the standard Drop screen via the shared convertExit='drop' exit.
  const renderPlugin = () => (
    <div className="ut-create-flow">
      <div className="ut-create-section-label">Export from a Figma plugin</div>
      <div className="ut-create-source-grid" role="list">
        {PLUGINS.map((p) => (
          <a
            key={p.label}
            className="ut-create-source-card"
            href={p.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="ut-create-source-title">{p.label}</span>
            <span className="ut-create-source-desc">{p.desc}</span>
          </a>
        ))}
      </div>
      <button type="button" className="createflow-dropzone" onClick={() => setConvertExit('drop')}>
        Once you have your HTML file, drop it here to publish
      </button>
    </div>
  );

  const renderPublished = () => (
    <div className="ut-create-flow">
      <div>
        <div className="ut-create-section-label">Choose a site</div>
        <div className="createflow-site-list" role="radiogroup" aria-label="Site">
          {(publishedSites || []).map((s) => {
            const isDraft = s.draft === true;
            return (
              <label key={s.id} className={`ut-create-site-row${selectedSiteId === s.id ? ' selected' : ''}`}>
                <input
                  className="ut-sr-only"
                  type="radio"
                  name="siteChoice"
                  value={s.id}
                  checked={selectedSiteId === s.id}
                  onChange={() => setSelectedSiteId(s.id)}
                />
                <span
                  className={`createflow-site-dot${isDraft ? ' draft' : ' live'}`}
                  title={isDraft ? 'Draft' : 'Live'}
                  aria-hidden="true"
                />
                <span className="ut-create-site-info">
                  <span className="ut-create-site-title">{s.title || s.generatedUrl}</span>
                  <span className="ut-create-site-url">{s.generatedUrl}.aeroapp.ai</span>
                </span>
                {s.userTesting?.enabled && <span className="aero-tag">Has a test</span>}
              </label>
            );
          })}
        </div>
        <div className="ut-create-hint">Draft sites are included; testers see the live version.</div>
      </div>
      {isTest && <TasksEditor tasks={tasks} onChange={setTasks} />}
    </div>
  );

  const renderStep2Body = () => {
    if (source === 'drop') return renderDrop();
    if (source === 'agent') return renderAgent();
    if (source === 'published') return renderPublished();
    if (source === 'figma') {
      // Shared exits: convert converges to the agent screen, convert/plugin both
      // converge to the standard drop screen.
      if (convertExit === 'drop') return renderDrop();
      if (convertExit === 'agent') return renderAgent();
      if (!figmaMode || figmaMode === 'embed') return renderFigmaEmbed();
      if (figmaMode === 'plugin') return renderPlugin();
      return renderConvert();
    }
    return null;
  };

  // Footer action varies by source. The agent / convert-agent paths act from
  // the body (Generate prompt, then Done), not a fake submit.
  const renderFooter = () => {
    const cancel = (
      <button type="button" className="aero-btn aero-btn-secondary" onClick={requestClose} disabled={busy}>
        {step === 1 ? 'Cancel' : 'Close'}
      </button>
    );

    const isAgentLike =
      source === 'agent' || (source === 'figma' && figmaMode === 'convert' && convertExit === 'agent');
    if (isAgentLike) {
      return (
        <>
          {!isPage && cancel}
          <button
            type="button"
            className="aero-btn aero-btn-primary"
            onClick={() => finish(liveLandingRef.current)}
            disabled={!live}
          >
            Done
          </button>
        </>
      );
    }

    if (source === 'drop' || (source === 'figma' && convertExit === 'drop')) {
      return (
        <>
          {!isPage && cancel}
          <button type="button" className="aero-btn aero-btn-primary" onClick={runDrop}
            disabled={busy || !droppedFiles || droppedFiles.length === 0}>
            {busy ? 'Publishing…' : (isTest ? 'Publish and continue' : 'Publish')}
          </button>
        </>
      );
    }

    if (source === 'figma' && (!figmaMode || figmaMode === 'embed')) {
      return (
        <>
          {!isPage && cancel}
          <button type="button" className="aero-btn aero-btn-primary" onClick={runFigmaEmbed}
            disabled={busy || !checkFigmaLink(figmaLink).ok}>
            {busy ? 'Setting up…' : (isTest ? 'Create test' : 'Create site')}
          </button>
        </>
      );
    }

    if (source === 'published') {
      return (
        <>
          {!isPage && cancel}
          <button type="button" className="aero-btn aero-btn-primary" onClick={runPublishedSite}
            disabled={busy || !selectedSiteId}>
            {busy ? 'Creating…' : 'Continue to setup'}
          </button>
        </>
      );
    }

    return isPage ? null : cancel;
  };

  const pageTitles = {
    drop: 'Upload your files',
    agent: 'Publish with a prompt',
    figma: 'Bring in your Figma prototype',
    published: 'Choose a site',
  };
  const stepTitle = step === 1
    ? (heading || (isTest ? 'New test' : 'New site'))
    : (isPage
      ? (pageTitles[source] || 'Set it up')
      : (source === 'figma' && figmaMode === 'embed' ? 'Paste your Figma link' : 'Set it up'));

  if (isPage) {
    const nameApplies = step === 2 && (
      source === 'drop'
      || (source === 'figma' && (!figmaMode || figmaMode === 'embed' || convertExit === 'drop'))
    );
    const siteWord = isTest ? 'prototype' : 'site';
    const next = step === 2 && agentActive
      ? [
        `${agentIsConvert ? 'Your AI tool' : 'Claude or ChatGPT'} publishes the files to Aero and replies with the live link.`,
        'This page updates on its own when they arrive. Closing it is fine; the site still shows under Sites.',
        agentSiteId
          ? `The ${siteWord} keeps the address ${agentSiteId}.aeroapp.ai. You can rename it later.`
          : `The ${siteWord} keeps the address Aero picks. You can rename it later.`,
      ]
      : isTest
        ? [
          hasPublishedSites ? 'Aero hosts the prototype under the test, or uses your live site.' : 'Aero hosts the prototype under the test.',
          'You get one link to share. Testers need no account and nothing to install.',
          'Each session shows up here when it ends, with a transcript and a summary about a minute later.',
        ]
        : [
          'It goes live at its own aeroapp.ai address, in seconds to a minute.',
          'Every update is a new version you can roll back to.',
          'Test it with people whenever you like, from Tests.',
        ];
    return (
      <div className="createflow-page">
        <div className="createflow-page-main">
          <div className="createflow-page-intro">
            <h2 className="createflow-page-title">
              {isTest ? 'What do you want to test?' : 'How is your site made?'}
            </h2>
            {isTest && (
              <p className="createflow-page-lead">
                {hasPublishedSites
                  ? 'A Figma prototype, an HTML file, a prompt to your AI tool, or one of your sites. Each one ends at one link to share.'
                  : 'A Figma prototype, an HTML file or a prompt to your AI tool. Each one ends at one link to share.'}
              </p>
            )}
          </div>
          <div className="ut-create-source-grid ut-create-source-grid-auto createflow-page-tiles" role="list">
            {sources.map((x) => (
              <button
                key={x.id}
                type="button"
                className={`ut-create-source-card${source === x.id ? ' selected' : ''}`}
                aria-pressed={source === x.id}
                onClick={() => (step === 1 ? selectSource(x.id) : switchSource(x.id))}
                disabled={busy}
              >
                <span className="ut-create-source-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {ICONS[x.id]}
                  </svg>
                </span>
                <span className="ut-create-source-title">
                  {x.title}
                  {x.tag && <span className="aero-tag aero-tag-blue" style={{ marginLeft: 6 }}>{x.tag}</span>}
                </span>
                <span className="ut-create-source-desc">{x.desc}</span>
              </button>
            ))}
          </div>

          {step === 2 && (
            <section className="createflow-page-panel" aria-live="polite" ref={panelRef}>
              <div className="createflow-step-head">
                <div className="createflow-step-titles">
                  <span className="createflow-step-title">{stepTitle}</span>
                </div>
              </div>
              {renderStep2Body()}
              {error && <p className="createflow-error">{error}</p>}
              {renderFooter() && <div className="createflow-page-actions">{renderFooter()}</div>}
            </section>
          )}
          {step === 1 && error && <p className="createflow-error">{error}</p>}
        </div>
        <aside className="createflow-page-aside">
          {nameApplies && (
            <div className="createflow-aside-fields">
              {nameField('Name', 'Optional. Defaults to the address')}
            </div>
          )}
          <div className="createflow-next">
            <div className="createflow-next-head">What happens next</div>
            <ol>
              {next.map((t) => <li key={t}>{t}</li>)}
            </ol>
          </div>
        </aside>
      </div>
    );
  }

  return ReactDOM.createPortal(
    <div className="ut-modal-root aero-modal-backdrop ut-theme" onClick={requestClose}>
      <div className="aero-modal createflow-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aero-modal-head">
          <h2>{heading || (isTest ? 'New test' : 'New site')}</h2>
          <button type="button" className="aero-btn aero-btn-ghost aero-btn-icon" onClick={requestClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="aero-modal-body createflow-body">
          {step === 2 && (
            <div className="createflow-step-head">
              <button type="button" className="aero-btn aero-btn-ghost aero-btn-sm createflow-back" onClick={goBack} disabled={busy}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>
              <div className="createflow-step-titles">
                <span className="createflow-step-title">{stepTitle}</span>
              </div>
            </div>
          )}

          {step === 1 ? renderSourceGrid() : renderStep2Body()}

          {error && <p className="createflow-error">{error}</p>}
        </div>

        <div className="aero-modal-foot">{renderFooter()}</div>
      </div>
    </div>,
    document.body
  );
}

export default CreateFlow;
